import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    Multiselect,
    Box,
    Alert,
} from '@amzn/awsui-components-console';
import React, { useEffect } from 'react';
import InputWithValidation from '../../bot-accounts/common/InputWithValidation';
import { onboardIAMRole } from '../apis';
import {
    ROLE_NAME_PREFIX,
    CallerArnsEditor,
    apiErrorMessage,
    apiErrorStatus,
    loadPrivateGroupOptions,
    toSubscribedGroups,
    validateCallerArns,
    validateTeam,
} from '../utils';
import { copyToClipboard, displayErrorContent, isValidInput } from '../../../lib/utils';
import { CLIENT_ERROR, SERVER_ERROR } from '../../../lib/constants';

export const ALREADY_ONBOARDED_MESSAGE =
    'This team is already onboarded. If it was disabled, contact @ds2-device-farm-dev.';

/**
 * Human-readable token lifetime, e.g. 3600 -> "1 hour".
 * @param {number} seconds - token lifetime in seconds
 */
export function formatLifetime(seconds) {
    if (seconds % 3600 === 0) {
        const hours = seconds / 3600;
        return `${hours} hour${hours === 1 ? '' : 's'}`;
    }
    const minutes = Math.round(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * What the onboarded team needs to call Device Farm with its own AWS credentials. The team
 * cannot see the Device Farm account, so this is its only instruction.
 * @param {object} props
 * @param {string} props.team - the onboarded team
 * @param {object} props.handback - the onboard response's handback
 */
export function Handback({ team, handback }) {
    return (
        <Container header={<Header variant="h2">Team {team} is onboarded</Header>}>
            <SpaceBetween direction="vertical" size="l">
                <Alert type="info">
                    Share these details with the team: they cannot see the Device Farm account, so this is their only
                    instruction for getting a token.
                </Alert>
                <div>
                    <Box variant="awsui-key-label">Delegated role ARN</Box>
                    {copyToClipboard(handback.delegatedRoleArn)}
                </div>
                <div>
                    <Box variant="awsui-key-label">Trusted callers</Box>
                    {(handback.trustedCallers || []).map((arn) => (
                        <Box key={arn}>{arn}</Box>
                    ))}
                </div>
                <div>
                    <Box variant="awsui-key-label">Policy to attach to each trusted caller</Box>
                    <Box variant="p">It lets the caller assume the delegated role with sts:AssumeRole.</Box>
                    {copyToClipboard(JSON.stringify(handback.callerPolicy, null, 2))}
                </div>
                <div>
                    <Box variant="awsui-key-label">Vending endpoint</Box>
                    <Box variant="p">
                        Call it with {handback.vendingHttpMethod} as the delegated role, signed with SigV4 for the{' '}
                        {handback.signingService} service.
                    </Box>
                    {copyToClipboard(handback.vendingEndpoint)}
                </div>
                <div>
                    <Box variant="awsui-key-label">Token</Box>
                    <Box>Bearer token, valid {formatLifetime(handback.tokenLifetimeSeconds)}.</Box>
                    {copyToClipboard(handback.authorizationHeader)}
                </div>
                <Box float="right">
                    <Button href="#/api-auth/iam-roles" variant="primary">
                        Back to IAM roles
                    </Button>
                </Box>
            </SpaceBetween>
        </Container>
    );
}

export default function OnboardIAMRole() {
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [team, setTeam] = React.useState('');
    const [teamError, setTeamError] = React.useState('');
    const [callerArns, setCallerArns] = React.useState(['']);
    const [callerArnErrors, setCallerArnErrors] = React.useState([]);
    const [description, setDescription] = React.useState('');
    const [ownerGroup, setOwnerGroup] = React.useState('');
    const [ownerGroupError, setOwnerGroupError] = React.useState('');
    const [groupsOptions, setGroupsOptions] = React.useState([]);
    const [groupsSelectedOptions, setGroupsSelectedOptions] = React.useState([]);
    const [groupsStatus, setGroupsStatus] = React.useState('pending');
    const [loading, setLoading] = React.useState(false);
    const [onboarded, setOnboarded] = React.useState(null);

    /**
     * validate input
     */
    const validateInput = () => {
        const nextTeamError = validateTeam(team.trim());
        const nextCallerArnErrors = validateCallerArns(callerArns);
        const nextOwnerGroupError = isValidInput(ownerGroup) ? '' : 'Owner group is required and should not be empty.';
        setTeamError(nextTeamError);
        setCallerArnErrors(nextCallerArnErrors);
        setOwnerGroupError(nextOwnerGroupError);
        return !nextTeamError && nextCallerArnErrors.every((error) => !error) && !nextOwnerGroupError;
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        if (groupsStatus === 'loading') {
            displayErrorContent(CLIENT_ERROR, 'Loading private groups, please wait.', setErrorMessage);
            return;
        }
        if (groupsStatus === 'error') {
            displayErrorContent(
                SERVER_ERROR,
                'Loading private groups failed, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                setErrorMessage
            );
            return;
        }
        setErrorMessage([]);
        if (!validateInput()) {
            return;
        }

        setLoading(true);
        try {
            const response = await onboardIAMRole(
                team.trim(),
                callerArns.map((arn) => arn.trim()),
                ownerGroup.trim(),
                description,
                toSubscribedGroups(groupsSelectedOptions)
            );
            setOnboarded({ team: team.trim(), handback: response['response']['handback'] });
        } catch (err) {
            console.error('Fail to onboard the IAM role with error', err);
            const status = apiErrorStatus(err);
            if (status === 409) {
                displayErrorContent(CLIENT_ERROR, ALREADY_ONBOARDED_MESSAGE, setErrorMessage);
            } else if (status === 400 || status === 403) {
                const data = err.response.data || {};
                displayErrorContent(
                    data.errorException || CLIENT_ERROR,
                    apiErrorMessage(err) || 'The request was rejected.',
                    setErrorMessage
                );
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to onboard this IAM role, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
        }
        setLoading(false);
    };

    /**
     * load the private groups the user administers, which are the ones it may subscribe a role to
     */
    useEffect(() => {
        let active = true;
        setGroupsStatus('loading');
        loadPrivateGroupOptions((group) => group.privilege === 'admin')
            .then((options) => {
                if (active) {
                    setGroupsOptions(options);
                    setGroupsStatus('finished');
                }
            })
            .catch((e) => {
                console.error('Fail to load private group(s) with error', e);
                if (active) {
                    setGroupsStatus('error');
                }
            });
        return () => {
            active = false;
        };
    }, []);

    const trimmedTeam = team.trim();
    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'IAM roles', href: '#/api-auth/iam-roles' },
                    { text: 'Onboard', href: '#/api-auth/iam-roles/onboard' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            {onboarded ? (
                <Handback team={onboarded.team} handback={onboarded.handback} />
            ) : (
                <form onSubmit={(e) => e.preventDefault()}>
                    <Form
                        actions={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button formAction="none" href="#/api-auth/iam-roles">
                                    Back
                                </Button>
                                <Button formAction="none" loading={loading} onClick={onClick} variant="primary">
                                    Confirm
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        <SpaceBetween direction="vertical" size="m">
                            <Container header={<Header variant="h2">Onboard IAM role</Header>}>
                                <SpaceBetween direction="vertical" size="m">
                                    <InputWithValidation
                                        label="Team"
                                        type="text"
                                        description={`Lowercase letters, digits and dashes. The IAM role is named ${ROLE_NAME_PREFIX}${
                                            trimmedTeam || '<team>'
                                        }.`}
                                        errorText={teamError}
                                        placeholder="Enter team name"
                                        value={team}
                                        setValue={setTeam}
                                        setError={setTeamError}
                                    ></InputWithValidation>
                                    <CallerArnsEditor
                                        arns={callerArns}
                                        onChange={(arns) => {
                                            setCallerArns(arns);
                                            setCallerArnErrors([]);
                                        }}
                                        itemErrors={callerArnErrors}
                                        lastItemHint="At least one trusted caller is required."
                                    />
                                    <InputWithValidation
                                        label="Owner group"
                                        type="text"
                                        description="LDAP group that owns this IAM role"
                                        errorText={ownerGroupError}
                                        placeholder="Enter owner group"
                                        value={ownerGroup}
                                        setValue={setOwnerGroup}
                                        setError={setOwnerGroupError}
                                    ></InputWithValidation>
                                    <FormField
                                        label={
                                            <span>
                                                Description <i>- optional</i>{' '}
                                            </span>
                                        }
                                    >
                                        <Input
                                            placeholder="Enter optional description"
                                            value={description}
                                            onChange={(event) => {
                                                setDescription(event.detail.value);
                                            }}
                                        />
                                    </FormField>
                                    <FormField
                                        label={
                                            <span>
                                                Subscribed private groups <i>- optional</i>{' '}
                                            </span>
                                        }
                                        description="The Common group is added automatically. This step may take few seconds when multiple private groups are selected."
                                    >
                                        <Multiselect
                                            selectedOptions={groupsSelectedOptions}
                                            expandToViewport
                                            placeholder="Select private groups to subscribe"
                                            loadingText="Loading groups"
                                            errorText="Error fetching groups"
                                            finishedText="End of all results"
                                            empty="No group found"
                                            filteringType="auto"
                                            virtualScroll={true}
                                            statusType={groupsStatus}
                                            options={groupsOptions}
                                            onChange={({ detail }) => {
                                                setGroupsSelectedOptions(detail.selectedOptions);
                                            }}
                                        />
                                    </FormField>
                                </SpaceBetween>
                            </Container>
                            <Flashbar items={errorMessage} />
                        </SpaceBetween>
                    </Form>
                </form>
            )}
        </SpaceBetween>
    );
}
